import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';

import DetectorQueries from './DetectorQueries';
import { isDiagnosticsFeatureEnabled } from './featureSwitch';
import IncidentCounters from './IncidentCounters';
import { hasInvertedDateRange, type IncidentListParams } from './incidentListParams';
import { INCIDENT_LIST_PAGE_SIZE, toIncidentListQueryArgs, type IncidentListQueryArgs } from './incidentListQueryArgs';
import { useListIncidentsQuery } from './incidentsApi';
import IncidentsListView from './IncidentsListView';
import { lastPage } from './IncidentsPager';
import { loadableOf } from './loadable';
import ResetDiagnostics from './ResetDiagnostics';
import RunConfirmationDialog from './RunConfirmationDialog';
import RunDetectionButton from './RunDetectionButton';
import RunDetectionNotice from './RunDetectionNotice';
import { useDetectionRun } from './useDetectionRun';
import { useIncidentListParams } from './useIncidentListParams';
import { useRunConfirmation } from './useRunConfirmation';

const withoutDates = (params: IncidentListParams): IncidentListParams => ({
  ...params,
  dateStart: null,
  dateEnd: null,
});

// A range whose end day precedes its start day is refused by the server, so it is left out of the
// request entirely while the fields show it as invalid. Every other filter still applies.
const queryArgsFor = (params: IncidentListParams): IncidentListQueryArgs =>
  toIncidentListQueryArgs(hasInvertedDateRange(params) ? withoutDates(params) : params);

function IncidentsList(): JSX.Element {
  const now = new Date();
  const enabled = isDiagnosticsFeatureEnabled();
  const { params, applyFilters, toggleSort, goToPage } = useIncidentListParams();
  const detectionRun = useDetectionRun(enabled);
  const runConfirmation = useRunConfirmation(enabled, detectionRun.start);
  const incidents = useListIncidentsQuery(queryArgsFor(params), { skip: !enabled });
  // `data` is whatever came back last, for any arguments; only `currentData` belongs to the request
  // on screen. Snapping on a previous filter's total costs a round trip to a page that then moves.
  const totalCount = incidents.currentData?.totalCount;

  useEffect(() => {
    if (totalCount === undefined) return;
    const last = lastPage(INCIDENT_LIST_PAGE_SIZE, totalCount);
    if (params.page > last) goToPage(last, { replace: true });
  }, [goToPage, params.page, totalCount]);

  if (!enabled) {
    return <Navigate to="/users" replace />;
  }

  return (
    <IncidentsListView
      incidents={loadableOf(incidents)}
      refreshing={incidents.isFetching}
      params={params}
      now={now}
      onFiltersChange={applyFilters}
      onSort={toggleSort}
      onPageChange={goToPage}
      actions={
        <>
          <DetectorQueries />
          <RunDetectionButton running={detectionRun.running} onRun={runConfirmation.request} />
          <RunConfirmationDialog
            confirmation={runConfirmation.confirmation}
            onConfirm={runConfirmation.confirm}
            onCancel={runConfirmation.cancel}
          />
          <ResetDiagnostics detectionRunning={detectionRun.running} />
        </>
      }
      runNotice={
        <RunDetectionNotice notice={detectionRun.notice} now={now} onRunAnyway={runConfirmation.offerRunAnyway} />
      }
      counters={<IncidentCounters now={now} />}
    />
  );
}

export default IncidentsList;
